import React, { Component } from "react";
import {
  Router,
  RouterProps,
  Styled
} from "../../../toolkit/decorators/Artifact";
import { CodigoPostalDispatcher } from "../../containers/codigoPostal/codigoPostal.dispatcher";
import { CodigoPostalStore } from "../../containers/codigoPostal/codigoPostal.store";
import s from "./styles.scss";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import ConfirmModal from "../common/confirmModal/confirmModal";
import { CodigoPostalTable } from "./CodigoPostal.table";
import { Localidad, LocalidadDuplicada } from "../../../types/codigoPostal";
import {
  errorNotification,
  warnNotification
} from "../../../utils/notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faSearch,
  faTimesCircle
} from "@fortawesome/free-solid-svg-icons";

interface Props
  extends CodigoPostalStore,
    CodigoPostalDispatcher,
    RouterProps<any> {}

interface State {
  confirmPopupOpen: boolean;
  confirmPopupId: number | null;
  modalOpen: boolean;
  editingLocalidad: Localidad | null;
  formValues: {
    codigoPostal: string;
    estado: string;
    municipio: string;
    localidad: string;
  };
  formValuesModal: {
    codigoPostal: string;
    estado: string;
    municipio: string;
    localidad: string;
  };
  duplicados: LocalidadDuplicada[];
  currentPage: number;
  currentSize: number;
  savingLocality: boolean;
}

@Router()
@Styled(s)
export default class CodigoPostalComponent extends Component<Props, State> {
  state = {
    confirmPopupOpen: false,
    confirmPopupId: null,
    modalOpen: false,
    editingLocalidad: null,
    formValues: {
      codigoPostal: "",
      estado: "",
      municipio: "",
      localidad: ""
    },
    formValuesModal: {
      codigoPostal: "",
      estado: "",
      municipio: "",
      localidad: ""
    },
    duplicados: [],
    currentPage: 0,
    currentSize: 50,
    savingLocality: false
  };

  handlePageChange = (page: number, size: number) => {
    this.setState({
      currentPage: page,
      currentSize: size
    });

    const { formValues } = this.state;
    const { codigoPostal, municipio, localidad } = formValues;

    if (municipio || codigoPostal || localidad) {
      const municipioId = municipio ? parseInt(municipio) : undefined;

      this.props.buscarLocalidades(
        municipioId,
        codigoPostal || undefined,
        localidad || undefined,
        page,
        size
      );
    }
  };

  componentDidMount() {
    this.fetchInitialData();
  }

  componentWillUnmount() {
    this.props.clearLocalidades();
  }

  fetchInitialData = async () => {
    try {
      await this.props.getAllActiveEstados();
    } catch (error) {
      console.error("Error al cargar estados:", error);
      errorNotification("Error al cargar la lista de estados");
    }
  };

  resetFilters = () => {
    this.setState({
      formValues: {
        codigoPostal: "",
        estado: "",
        municipio: "",
        localidad: ""
      }
    });

    this.props.clearMunicipios();
    this.props.clearLocalidades();
  };

  handleInputChange = e => {
    const { name, value } = e.target;

    if (name === "estado") {
      this.setState({
        formValues: {
          ...this.state.formValues,
          [name]: value,
          municipio: ""
        }
      });

      if (value) {
        this.props.getAllActiveMunicipiosByEstado(parseInt(value));
      } else {
        this.props.clearMunicipios();
      }
    } else {
      this.setState({
        formValues: {
          ...this.state.formValues,
          [name]: value
        }
      });
    }
  };

  handleModalInputChange = e => {
    const { name, value } = e.target;
    this.setState({
      formValuesModal: {
        ...this.state.formValuesModal,
        [name]: value
      }
    });

    if (name === "estado" && value) {
      this.props.getAllActiveMunicipiosByEstado(parseInt(value));
      this.setState({ duplicados: [] });
    }
  };

  handleSubmit = e => {
    e.preventDefault();
    const {
      codigoPostal,
      municipio,
      localidad,
      estado
    } = this.state.formValues;

    if (!municipio && !codigoPostal && !localidad) {
      if (estado != "") {
        errorNotification("Seleccione al menos un municipio");
      } else {
        errorNotification("Debe proporcionar al menos un criterio de búsqueda");
      }
      return;
    }
    this.setState({ currentPage: 0 });

    const municipioId = municipio ? parseInt(municipio) : undefined;

    this.props
      .buscarLocalidades(
        municipio ? municipioId : undefined,
        codigoPostal || undefined,
        localidad || undefined,
        0,
        this.state.currentSize
      )
      .then(response => {
        const localidadesData = response.localidades;
        const isEmpty = Array.isArray(localidadesData)
          ? localidadesData.length === 0
          : !localidadesData ||
            !localidadesData.content ||
            localidadesData.content.length === 0;

        if (isEmpty) {
          warnNotification(
            "No se encontraron localidades con los criterios seleccionados"
          );
        }
      })
      .catch(error => {
        console.error("Error en la búsqueda:", error);
        errorNotification("Ocurrió un error al buscar localidades");
      });
  };

  handleModalSubmit = async e => {
    e.preventDefault();
    this.setState(state => ({ ...state, savingLocality: true }));
    const {
      codigoPostal,
      estado,
      municipio,
      localidad
    } = this.state.formValuesModal;

    const camposFaltantes = [];

    if (!codigoPostal || codigoPostal.trim() === "") {
      camposFaltantes.push("Código Postal");
    }

    if (!estado || estado === "") {
      camposFaltantes.push("Estado");
    }

    if (!municipio || municipio === "") {
      camposFaltantes.push("Municipio");
    }

    if (!localidad || localidad.trim() === "") {
      camposFaltantes.push("Localidad");
    }

    if (camposFaltantes.length > 0) {
      const mensaje =
        camposFaltantes.length === 1
          ? `El campo ${camposFaltantes[0]} es obligatorio`
          : `Los siguientes campos son obligatorios: ${camposFaltantes.join(
              ", "
            )}`;

      errorNotification(mensaje);
      this.setState(state => ({ ...state, savingLocality: false }));
      return;
    }

    try {
      const estadoId = parseInt(estado);
      const municipioId = parseInt(municipio);
      const codigoId = codigoPostal;

      if (isNaN(estadoId) || isNaN(municipioId)) {
        errorNotification("Estado y Municipio deben ser válidos");
        this.setState(state => ({ ...state, savingLocality: false }));
        return;
      }

      const response = await this.props.getLocalidadesDuplicadas(
        municipioId,
        codigoId,
        localidad
      );

      if (
        response &&
        this.props.localidadesDuplicadas &&
        this.props.localidadesDuplicadas.length > 0
      ) {
        errorNotification("Error, ya existe la localidad");
        this.setState(state => ({ ...state, savingLocality: false }));
        return;
      }

      const localidadData = {
        codigoPostal: codigoId,
        estadoId: estadoId,
        municipioId: municipioId,
        localidad: localidad
      };

      let resultado;
      if (this.state.editingLocalidad) {
        const localidadId = this.state.editingLocalidad.localidadId;
        resultado = await this.props.actualizarLocalidad(localidadId, {
          ...localidadData,
          localidadId: localidadId
        });
      } else {
        resultado = await this.props.guardarLocalidad(localidadData);
      }

      this.setState(state => ({
        ...state,
        modalOpen: false,
        editingLocalidad: null,
        formValuesModal: {
          codigoPostal: "",
          estado: "",
          municipio: "",
          localidad: ""
        },
        savingLocality: false
      }));

      if (
        this.state.formValues.codigoPostal != "" ||
        this.state.formValues.municipio != "" ||
        this.state.formValues.localidad != ""
      ) {
        const fMunicipioId = this.state.formValues.municipio
          ? parseInt(this.state.formValues.municipio)
          : undefined;
        this.props.buscarLocalidades(
          fMunicipioId,
          this.state.formValues.codigoPostal || undefined,
          this.state.formValues.localidad || undefined,
          this.state.currentPage,
          this.state.currentSize
        );
      }
    } catch (error) {
      console.error("Error al guardar/actualizar la localidad:", error);
      errorNotification("Error al guardar la localidad");
      this.setState(state => ({ ...state, savingLocality: false }));
    }
  };

  onAddClick = () => {
    this.setState({
      modalOpen: true,
      editingLocalidad: null,
      formValuesModal: {
        codigoPostal: "",
        estado: "",
        municipio: "",
        localidad: ""
      }
    });
  };

  onEditClick = async (localidad: Localidad) => {
    if (localidad.estado) {
      const estadoEncontrado = this.props.estados.find(
        e => e.nombre === localidad.estado
      );
      if (estadoEncontrado) {
        await this.props.getAllActiveMunicipiosByEstado(
          estadoEncontrado.estadoId
        );
      }
    }

    this.setState({
      modalOpen: true,
      editingLocalidad: localidad,
      formValuesModal: {
        codigoPostal: localidad.codigoPostal || "",
        estado: this.getEstadoIdByNombre(localidad.estado),
        municipio: this.getMunicipioIdByNombre(localidad.municipio),
        localidad: localidad.nombre || ""
      }
    });
  };

  getEstadoIdByNombre(nombre: string): string {
    const estado = this.props.estados.find(e => e.nombre === nombre);
    return estado ? estado.estadoId.toString() : "";
  }

  getMunicipioIdByNombre(nombre: string): string {
    const municipio = this.props.municipios.find(m => m.nombre === nombre);
    return municipio ? municipio.municipioId.toString() : "";
  }

  onDeleteClick = (id: number) => {
    this.setState({
      confirmPopupOpen: true,
      confirmPopupId: id
    });
  };

  confirmDelete = async () => {
    const { confirmPopupId, formValues } = this.state;
    await this.props.eliminarLocalidad(confirmPopupId);

    const fMunicipioId = formValues.municipio
      ? parseInt(formValues.municipio)
      : undefined;
    this.props.buscarLocalidades(
      fMunicipioId,
      formValues.codigoPostal || undefined,
      formValues.localidad || undefined,
      this.state.currentPage,
      this.state.currentSize
    );

    this.setState({
      confirmPopupOpen: false,
      confirmPopupId: null
    });
  };

  toggleModal = () => {
    this.setState({
      modalOpen: !this.state.modalOpen,
      editingLocalidad: null,
      formValuesModal: {
        codigoPostal: "",
        estado: "",
        municipio: "",
        localidad: ""
      },
      duplicados: []
    });
  };

  render() {
    const { estados, municipios, localidades, searching } = this.props;
    const {
      formValues,
      formValuesModal,
      modalOpen,
      editingLocalidad
    } = this.state;

    return (
      <Container className="mb-5">
        <Form onSubmit={this.handleSubmit}>
          <Row className="mt-2">
            <Col md={3}>
              <FormGroup>
                <Label for="codigoPostal">Código Postal:</Label>
                <Input
                  type="text"
                  name="codigoPostal"
                  id="codigoPostal"
                  placeholder="Ingrese código postal"
                  value={formValues.codigoPostal}
                  onChange={this.handleInputChange}
                  maxLength={5}
                />
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="estado">Estado:</Label>
                <Input
                  type="select"
                  name="estado"
                  id="estado"
                  value={formValues.estado}
                  onChange={this.handleInputChange}
                  disabled={!estados || estados.length === 0}
                >
                  <option value="">Seleccione estado</option>
                  {estados &&
                    estados.map(estado => (
                      <option key={estado.estadoId} value={estado.estadoId}>
                        {estado.nombre}
                      </option>
                    ))}
                </Input>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="municipio">Municipio:</Label>
                <Input
                  type="select"
                  name="municipio"
                  id="municipio"
                  value={formValues.municipio}
                  onChange={this.handleInputChange}
                  disabled={
                    !formValues.estado || !municipios || municipios.length === 0
                  }
                >
                  <option value="">Seleccione municipio</option>
                  {municipios &&
                    municipios.map(municipio => (
                      <option
                        key={municipio.municipioId}
                        value={municipio.municipioId}
                      >
                        {municipio.nombre}
                      </option>
                    ))}
                </Input>
              </FormGroup>
            </Col>

            <Col md={3}>
              <FormGroup>
                <Label for="localidad">Localidad:</Label>
                <Input
                  type="text"
                  name="localidad"
                  id="localidad"
                  placeholder="Ingrese nombre de localidad"
                  value={formValues.localidad}
                  onChange={this.handleInputChange}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={{ size: 2, offset: 8 }}>
              <Button
                color="secondary"
                type="button"
                outline
                block
                onClick={this.resetFilters}
                disabled={searching}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                Limpiar Filtros
              </Button>
            </Col>
            <Col md="2">
              <Button
                style={{ backgroundColor: "#6c757d" }}
                type="submit"
                block
                disabled={searching}
              >
                {searching ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2 mb-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Buscando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    Buscar
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>

        <div className="mt-4">
          <CodigoPostalTable
            page={localidades}
            onAddClick={this.onAddClick}
            onEditClick={this.onEditClick}
            onDeleteClick={this.onDeleteClick}
            onPageChange={this.handlePageChange}
          />
        </div>

        <ConfirmModal
          title="Confirmar eliminación"
          message="¿Está seguro de eliminar esta localidad?"
          isOpen={this.state.confirmPopupOpen}
          ok={this.confirmDelete}
          cancel={() => this.setState({ confirmPopupOpen: false })}
        />

        <Modal
          isOpen={modalOpen}
          toggle={this.toggleModal}
          size="lg"
          backdrop="static"
        >
          <ModalHeader toggle={this.toggleModal}>
            {editingLocalidad ? "Editar localidad" : "Crear nueva localidad"}
          </ModalHeader>
          <ModalBody>
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="modalCodigoPostal">Código Postal</Label>
                    <Input
                      type="text"
                      name="codigoPostal"
                      id="modalCodigoPostal"
                      placeholder="Ingrese código postal"
                      value={formValuesModal.codigoPostal}
                      onChange={this.handleModalInputChange}
                      maxLength={5}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="modalEstado">Estado:</Label>
                    <Input
                      type="select"
                      name="estado"
                      id="modalEstado"
                      value={formValuesModal.estado}
                      onChange={this.handleModalInputChange}
                      disabled={!estados || estados.length === 0}
                    >
                      <option value="">Seleccione estado:</option>
                      {estados &&
                        estados.map(estado => (
                          <option key={estado.estadoId} value={estado.estadoId}>
                            {estado.nombre}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="modalMunicipio">Municipio:</Label>
                    <Input
                      type="select"
                      name="municipio"
                      id="modalMunicipio"
                      value={formValuesModal.municipio}
                      onChange={this.handleModalInputChange}
                      disabled={
                        !formValuesModal.estado ||
                        !municipios ||
                        municipios.length === 0
                      }
                    >
                      <option value="">Seleccione municipio:</option>
                      {municipios &&
                        municipios.map(municipio => (
                          <option
                            key={municipio.municipioId}
                            value={municipio.municipioId}
                          >
                            {municipio.nombre}
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="modalLocalidad">Localidad:</Label>
                    <Input
                      type="text"
                      name="localidad"
                      id="modalLocalidad"
                      placeholder="Ingrese nombre de localidad"
                      value={formValuesModal.localidad}
                      onChange={this.handleModalInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              onClick={this.toggleModal}
              disabled={this.state.savingLocality}
            >
              Cancelar
            </Button>
            <Button
              color="dark"
              onClick={this.handleModalSubmit}
              disabled={this.state.savingLocality}
            >
              {editingLocalidad ? "Actualizar" : "Guardar"}
              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }
}
